/* eslint-disable react-refresh/only-export-components */
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute'
import { AdminProtectedRoute } from '@/features/admin/components/AdminProtectedRoute'
import { AuthenticatedLayout } from '@/components/layout/AuthenticatedLayout'

// Lazy load pages (will be created in later phases)
import { lazy, Suspense } from 'react'

const LoginPage = lazy(() => import('@/features/auth/components/LoginPage'))
const RegisterPage = lazy(() => import('@/features/auth/components/RegisterPage'))
const CreateOrgPage = lazy(() => import('@/features/auth/components/CreateOrgPage'))
const AuthCallbackPage = lazy(() => import('@/features/auth/components/AuthCallbackPage'))
const NotionCallbackPage = lazy(() => import('@/features/auth/components/NotionCallbackPage'))
const InviteSignupPage = lazy(() => import('@/features/auth/components/InviteSignupPage'))
const DashboardPage = lazy(() => import('@/features/dashboard/components/DashboardPage'))
const DealsPage = lazy(() => import('@/features/dashboard/components/DealsPage'))
const DealChatPage = lazy(() => import('@/features/deals/components/DealChatPage'))
const WorkbenchPage = lazy(() => import('@/features/workbench/components/WorkbenchPage'))
const AnalysisReportPage = lazy(() => import('@/features/deals/components/report/AnalysisReportPage'))
const ImpactXFrameworkPage = lazy(() => import('@/features/deals/components/ImpactXFrameworkPage'))
const FundsPage = lazy(() => import('@/features/funds/components/FundsPage'))
const PortfolioPage = lazy(() => import('@/features/dashboard/components/PortfolioPage'))
const TeamPage = lazy(() => import('@/features/team/components/TeamPage'))
const ContactsPage = lazy(() => import('@/features/contacts/components/ContactsPage'))
const SettingsPage = lazy(() => import('@/features/settings/components/SettingsPage'))
const ScoringPage = lazy(() => import('@/features/scoring/components/ScoringPage'))
const WhatsNewPage = lazy(() => import('@/features/whats-new/components/WhatsNewPage'))

// Admin pages
const AdminChoosePage = lazy(() => import('@/features/admin/components/AdminChoosePage'))
const AdminLayout = lazy(() => import('@/features/admin/components/AdminLayout'))
const AdminDashboardPage = lazy(() => import('@/features/admin/components/AdminDashboardPage'))
const AdminOrgDetailPage = lazy(() => import('@/features/admin/components/AdminOrgDetailPage'))
const AdminInsightsPage = lazy(() => import('@/features/admin/components/AdminInsightsPage'))
const AdminInfrastructurePage = lazy(() => import('@/features/admin/components/AdminInfrastructurePage'))

// Loading component
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  )
}

// Suspense wrapper
function SuspensePage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>
}

export const router = createBrowserRouter([
  {
    path: '/privacy',
    loader: () => {
      window.location.href = 'https://arceusxventures.com/privacy'
      return null
    },
    element: null,
  },
  {
    path: '/terms',
    loader: () => {
      window.location.href = 'https://arceusxventures.com/terms'
      return null
    },
    element: null,
  },
  {
    path: '/login',
    element: (
      <SuspensePage>
        <LoginPage />
      </SuspensePage>
    ),
  },
  {
    path: '/register',
    element: (
      <SuspensePage>
        <RegisterPage />
      </SuspensePage>
    ),
  },
  {
    path: '/create-org',
    element: (
      <SuspensePage>
        <CreateOrgPage />
      </SuspensePage>
    ),
  },
  {
    path: '/auth/callback',
    element: (
      <SuspensePage>
        <AuthCallbackPage />
      </SuspensePage>
    ),
  },
  {
    path: '/auth/notion/callback',
    element: (
      <SuspensePage>
        <NotionCallbackPage />
      </SuspensePage>
    ),
  },
  {
    path: '/invite/signup',
    element: (
      <SuspensePage>
        <InviteSignupPage />
      </SuspensePage>
    ),
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AuthenticatedLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspensePage>
            <DashboardPage />
          </SuspensePage>
        ),
      },
      // Deals routes
      {
        path: 'deals',
        element: (
          <SuspensePage>
            <DealsPage />
          </SuspensePage>
        ),
      },
      {
        path: 'deals/:dealId',
        element: (
          <SuspensePage>
            <DealChatPage />
          </SuspensePage>
        ),
      },
      {
        path: 'deals/:dealId/report',
        element: (
          <SuspensePage>
            <AnalysisReportPage />
          </SuspensePage>
        ),
      },
      {
        path: 'deals/:dealId/workbench',
        element: (
          <SuspensePage>
            <WorkbenchPage />
          </SuspensePage>
        ),
      },
      {
        path: 'impactx-framework',
        element: (
          <SuspensePage>
            <ImpactXFrameworkPage />
          </SuspensePage>
        ),
      },
      {
        path: 'funds',
        element: (
          <SuspensePage>
            <FundsPage />
          </SuspensePage>
        ),
      },
      {
        path: 'portfolio',
        element: (
          <SuspensePage>
            <PortfolioPage />
          </SuspensePage>
        ),
      },
      {
        path: 'contacts',
        element: (
          <SuspensePage>
            <ContactsPage />
          </SuspensePage>
        ),
      },
      {
        path: 'team',
        element: (
          <SuspensePage>
            <TeamPage />
          </SuspensePage>
        ),
      },
      {
        path: 'settings',
        element: (
          <SuspensePage>
            <SettingsPage />
          </SuspensePage>
        ),
      },
      {
        path: 'scoring',
        element: (
          <SuspensePage>
            <ScoringPage />
          </SuspensePage>
        ),
      },
      {
        path: 'whats-new',
        element: (
          <SuspensePage>
            <WhatsNewPage />
          </SuspensePage>
        ),
      },
    ],
  },
  // Admin routes
  {
    path: '/admin/choose',
    element: (
      <ProtectedRoute>
        <SuspensePage>
          <AdminChoosePage />
        </SuspensePage>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin',
    element: (
      <AdminProtectedRoute>
        <SuspensePage>
          <AdminLayout />
        </SuspensePage>
      </AdminProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspensePage>
            <AdminDashboardPage />
          </SuspensePage>
        ),
      },
      {
        path: 'org/:orgId',
        element: (
          <SuspensePage>
            <AdminOrgDetailPage />
          </SuspensePage>
        ),
      },
      {
        path: 'insights',
        element: (
          <SuspensePage>
            <AdminInsightsPage />
          </SuspensePage>
        ),
      },
      {
        path: 'infrastructure',
        element: (
          <SuspensePage>
            <AdminInfrastructurePage />
          </SuspensePage>
        ),
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
])
